import { defineStore } from 'pinia';
import appConfig from '@/app.config';

/**
 * Rutas públicas que se renderizan SIEMPRE con un tema fijo, sin importar la
 * preferencia del usuario.
 *
 * Son superficies de marca, no de trabajo: la landing recibe tráfico frío de
 * Instagram y tiene que verse igual que el anuncio que la persona acaba de
 * tocar. Si respetara el `localStorage` de un visitante que volvió, el mismo
 * anuncio caería a veces en claro y a veces en oscuro, que es justo lo que se
 * intenta evitar.
 *
 * Todas van en claro para acompañar la línea del brand book de Instagram
 * (índigo #4F46E5 sobre lienzo claro, Poppins + Inter). Las legales entran acá
 * porque casi siempre se llegan desde la landing o desde el banner de cookies:
 * si siguieran la preferencia guardada, el visitante anónimo — cuyo default es
 * oscuro — saltaría de una página clara a una oscura al tocar "Leer más".
 *
 * Este mapa es la única fuente de verdad: lo usa tanto el arranque (para pintar
 * bien antes de que el router esté listo y evitar el flash) como cada
 * navegación posterior.
 */
export const FORCED_THEME_BY_PATH = new Map([
  ['/', 'light'],
  ['/politicas-de-privacidad', 'light'],
  ['/terminos-y-condiciones', 'light'],
]);

/** Modo por defecto en la primera visita (sin nada en localStorage). */
const DEFAULT_MODE = appConfig.theme?.default_mode === 'light' ? 'light' : 'dark';

function readStoredTheme() {
  const stored = localStorage.getItem('theme');
  return stored === 'light' || stored === 'dark' ? stored : DEFAULT_MODE;
}

/** Devuelve el tema forzado por la ruta, o null si la ruta respeta al usuario. */
export function forcedThemeForPath(path) {
  return FORCED_THEME_BY_PATH.get(path) ?? null;
}

/**
 * Alinea la barra del navegador / status bar de la PWA con el fondo real.
 * Se lee del token en vez de una tabla propia para que no se desincronice
 * cuando cambie la paleta.
 */
function syncBrowserThemeColor() {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const bg = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-background')
    .trim();
  if (bg) meta.setAttribute('content', bg);
}

export const useThemeStore = defineStore('theme', {
  state: () => ({
    /** Preferencia del usuario. Puede no coincidir con lo que se ve si la ruta fuerza un tema. */
    theme: readStoredTheme(),
    /** Tema impuesto por la ruta actual ('light' | 'dark' | null). */
    forcedTheme: null,
  }),
  getters: {
    /** Tema realmente pintado en pantalla. */
    effectiveTheme: (state) => state.forcedTheme || state.theme,
    /** Si está en true, el toggle no cambia nada visible y conviene ocultarlo. */
    isThemeLocked: (state) => state.forcedTheme !== null,
  },
  actions: {
    toggleTheme() {
      this.setTheme(this.theme === 'light' ? 'dark' : 'light');
    },
    setTheme(theme) {
      if (theme !== 'light' && theme !== 'dark') return;
      this.theme = theme;
      localStorage.setItem('theme', theme);
      this.applyTheme();
    },
    /** Reaplica el tema vigente sin recalcular la ruta. */
    applyTheme() {
      document.documentElement.classList.toggle('dark', this.effectiveTheme === 'dark');
      syncBrowserThemeColor();
    },
    /**
     * Recalcula el tema para una ruta. Acepta el objeto de ruta de vue-router
     * o directamente un path.
     */
    applyThemeForRoute(route) {
      const path = typeof route === 'string' ? route : route?.path;
      this.forcedTheme = forcedThemeForPath(path);
      this.applyTheme();
    },
    initTheme() {
      this.theme = readStoredTheme();
      // Se resuelve contra la URL real porque el router todavía no está listo:
      // así la landing arranca en su tema fijo, sin flash del tema del usuario.
      this.forcedTheme = forcedThemeForPath(window.location.pathname);
      this.applyTheme();
    },
  },
});
